import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pill, User, Plus, Zap, Pencil } from "lucide-react";
import api, { fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useFirma, OperatoreLocked } from "../context/FirmaContext";
import { toast } from "sonner";

const dedupeOrari = (s) => [...new Set((s || "").split(",").map((x) => x.trim()).filter(Boolean))].sort().join(", ");
const fmtDT = (iso) => { try { const d = new Date(iso); return `${d.toLocaleDateString("it-IT")} ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`; } catch { return "—"; } };
const GROUPS = [["attive", "Quotidiana · attive", "#16a34a", "#dcfce7"], ["sospese", "Quotidiana · sospese", "#d97706", "#fef3c7"], ["eliminate", "Quotidiana · eliminate", "#dc2626", "#fee2e2"]];

export default function TerapieStorico() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { opName } = useFirma();
  const { data: scheda } = useQuery({ queryKey: ["scheda", id], queryFn: async () => (await api.get(`/ospiti/${id}/scheda`)).data });
  const { data: ter = [] } = useQuery({ queryKey: ["ter-storico", id], queryFn: async () => (await api.get(`/terapie/storico/${id}`)).data });
  const [operatori, setOperatori] = useState([]);
  const [search, setSearch] = useState("");
  const [addDlg, setAddDlg] = useState(false);
  const [form, setForm] = useState({});
  const [act, setAct] = useState(null); // { terapia, tipo:'sospendi'|'riattiva'|'elimina', operatore_nome }

  const o = scheda?.ospite;
  useEffect(() => { if (o) api.get("/operatori", { params: { structure_id: o.structure_id } }).then((r) => setOperatori(r.data)); }, [o]);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["ter-storico", id] }); qc.invalidateQueries({ queryKey: ["terapie"] }); };

  if (!scheda) return <div className="text-muted-foreground">Caricamento storico...</div>;
  const q = search.trim().toLowerCase();
  const matchS = (t) => (!q || (t.farmaco || "").toLowerCase().includes(q));
  const byUpd = (a, b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || "");
  const groupOrd = (g) => ter.filter((t) => t.tipo === "ordinaria").filter(matchS)
    .filter((t) => g === "eliminate" ? t.status === "eliminato" : t.status !== "eliminato" && (g === "sospese" ? t.stato === "sospesa" : t.stato !== "sospesa"))
    .sort(byUpd);
  const bisognoAttuali = ter.filter((t) => t.tipo === "bisogno" && t.status !== "eliminato" && t.stato !== "sospesa").filter(matchS).sort(byUpd);

  const OP = (val, set, tid) => (opName
    ? <OperatoreLocked name={opName} testid={tid} />
    : <Select value={val || ""} onValueChange={set}><SelectTrigger data-testid={tid}><SelectValue placeholder="Seleziona operatore" /></SelectTrigger>
        <SelectContent>{operatori.map((op) => <SelectItem key={op.id} value={`${op.nome} ${op.cognome}`}>{op.nome} {op.cognome} ({op.qualifica})</SelectItem>)}</SelectContent></Select>
  );

  const openAdd = (tipo = "ordinaria") => { setForm({ tipo, stato: "attiva", operatore_nome: opName || "" }); setAddDlg(true); };
  const saveAdd = async () => {
    if (!form.farmaco?.trim()) { toast.error("Inserisci il farmaco."); return; }
    if (!form.operatore_nome) { toast.error("Seleziona l'operatore."); return; }
    try { await api.post("/terapie", { ...form, ospite_id: o.id, ospite_nome: `${o.nome} ${o.cognome}`, structure_id: o.structure_id }); toast.success("Terapia aggiunta"); setAddDlg(false); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const doAct = async () => {
    if (!act.operatore_nome) { toast.error("Seleziona l'operatore."); return; }
    try {
      if (act.tipo === "elimina") { await api.put(`/terapie/${act.terapia.id}`, { operatore_nome: act.operatore_nome }); await api.delete(`/terapie/${act.terapia.id}`); }
      else await api.put(`/terapie/${act.terapia.id}`, { stato: act.tipo === "sospendi" ? "sospesa" : "attiva", operatore_nome: act.operatore_nome });
      toast.success("Fatto"); setAct(null); refresh();
    } catch { toast.error("Errore"); }
  };

  const TerCard = ({ t, color, canSuspend }) => (
    <div key={t.id} className="rounded-lg border bg-white p-3" data-testid={`storico-ter-${t.id}`} style={{ borderLeft: `3px solid ${color}` }}>
      <div className="font-bold text-sm">{t.farmaco}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{t.dosaggio}{dedupeOrari(t.orari) && <span className="tabnum"> · {dedupeOrari(t.orari)}</span>}<span className="capitalize"> · {t.tipo === "ordinaria" ? "quotidiana" : "al bisogno"}</span></div>
      <div className="text-[11px] text-muted-foreground mt-1">Dal {fmtDate(t.data_inizio)}{t.data_fine ? ` al ${fmtDate(t.data_fine)}` : ""}</div>
      <div className="mt-2 pt-2 border-t flex items-center gap-1.5 text-xs font-semibold" style={{ color }}>
        <User size={13} /><span>{t.operatore_nome || t.updated_by || t.created_by || "—"}</span>
        <span className="ml-auto font-normal text-muted-foreground tabnum">{fmtDT(t.updated_at || t.created_at)}</span>
      </div>
    </div>
  );

  return (
    <div>
      <Button variant="ghost" onClick={() => nav("/terapie")} className="mb-3" data-testid="storico-back"><ArrowLeft size={16} className="mr-1" /> Torna alle terapie</Button>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Pill size={22} /> Storico terapie</h1>
        <Button onClick={() => nav(`/impostazione-terapie?ospite=${id}`)} data-testid="storico-add" className="font-semibold"><Pencil size={16} className="mr-1" /> Modifica in Impostazione terapie</Button>
      </div>
      <button onClick={() => nav(`/ospiti/${o.id}`)} className="text-blue-700 font-semibold hover:underline mb-4 inline-block" data-testid="storico-nome">{o.nome} {o.cognome} · Camera {o.camera || "—"}</button>

      <div className="gr-card p-3 mb-4 flex flex-wrap items-center gap-3">
        <Input placeholder="Cerca farmaco..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-56" data-testid="storico-search" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {GROUPS.map(([key, label, color, bg]) => {
            const items = groupOrd(key);
            return (
              <div key={key} className="rounded-xl border overflow-hidden" data-testid={`storico-group-${key}`}>
                <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: bg }}>
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
                  <span className="font-head font-bold" style={{ color }}>{label}</span>
                  <span className="ml-auto text-xs font-bold" style={{ color }} data-testid={`storico-count-${key}`}>{items.length}</span>
                </div>
                <div className="p-3 grid md:grid-cols-2 gap-2">
                  {items.length === 0 && <p className="text-sm text-muted-foreground py-2">Nessuna terapia</p>}
                  {items.map((t) => <TerCard key={t.id} t={t} color={color} canSuspend={key === "attive"} />)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="lg:col-span-1">
          <div className="rounded-xl border overflow-hidden" data-testid="storico-group-bisogno">
            <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: "#fef3c7" }}>
              <Zap size={15} style={{ color: "#d97706" }} />
              <span className="font-head font-bold" style={{ color: "#d97706" }}>Terapie al bisogno</span>
              <span className="ml-auto text-xs font-bold" style={{ color: "#d97706" }} data-testid="storico-count-bisogno">{bisognoAttuali.length}</span>
            </div>
            <div className="p-3 space-y-2">
              {bisognoAttuali.length === 0 && <p className="text-sm text-muted-foreground py-2">Nessuna terapia al bisogno attuale</p>}
              {bisognoAttuali.map((t) => <TerCard key={t.id} t={t} color="#d97706" canSuspend={true} />)}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={addDlg} onOpenChange={setAddDlg}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nuova terapia</DialogTitle><DialogDescription>Aggiungi una terapia a {o.nome} {o.cognome}. Farmaco e operatore obbligatori.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs font-semibold mb-1 block">Farmaco *</Label><Input value={form.farmaco || ""} onChange={(e) => setForm((p) => ({ ...p, farmaco: e.target.value }))} data-testid="add-farmaco" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Dosaggio</Label><Input value={form.dosaggio || ""} onChange={(e) => setForm((p) => ({ ...p, dosaggio: e.target.value }))} /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Orari</Label><Input value={form.orari || ""} onChange={(e) => setForm((p) => ({ ...p, orari: e.target.value }))} placeholder="es. 08:00, 20:00" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm((p) => ({ ...p, tipo: v }))}><SelectTrigger data-testid="add-tipo"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ordinaria">Quotidiana</SelectItem><SelectItem value="bisogno">Al bisogno</SelectItem></SelectContent></Select></div>
            <div><Label className="text-xs font-semibold mb-1 block">Operatore *</Label>{OP(form.operatore_nome, (v) => setForm((p) => ({ ...p, operatore_nome: v })), "add-operatore")}</div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddDlg(false)}>Annulla</Button><Button onClick={saveAdd} data-testid="add-save" className="font-semibold">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!act} onOpenChange={(v) => { if (!v) setAct(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{act?.tipo === "elimina" ? "Elimina terapia" : act?.tipo === "sospendi" ? "Sospendi terapia" : "Riattiva terapia"}</DialogTitle>
            <DialogDescription>{act ? `${act.terapia.farmaco} — indica l'operatore che effettua l'azione.` : ""}</DialogDescription></DialogHeader>
          <div><Label className="text-xs font-semibold mb-1 block">Operatore *</Label>{OP(act?.operatore_nome, (v) => setAct((p) => ({ ...p, operatore_nome: v })), "act-operatore")}</div>
          <DialogFooter><Button variant="outline" onClick={() => setAct(null)}>Annulla</Button><Button onClick={doAct} data-testid="act-confirm" className={`font-semibold ${act?.tipo === "elimina" ? "bg-destructive hover:bg-destructive/90" : ""}`}>Conferma</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
